import React, { useState, useEffect, useCallback } from "react";
import { Main } from "layouts";
import { client_v2 } from "utils/api";
import { getApiErrorMessage } from "utils/api";
import { PanelLoader } from "components";
import { toastr } from "react-redux-toastr";

import { ReactComponent as GdnIcon } from "icons/gdn.svg";
import CreateFastShipmentModal from "./CreateFastShipmentModal";
import ActiveFastShipmentPacks from "./components/ActiveFastShipmentPacks";
import GdnCartBox from "./components/GdnCartBox";

/** Kolejność sekcji; rozmiary spoza listy trafiają na koniec alfabetycznie. */
const BOX_SECTION_ORDER = ["box_24", "box_12", "box_6", "box_4", "none", "heavy"];

const sortSectionKeys = (keys) =>
  [...keys].sort((a, b) => {
    const ia = BOX_SECTION_ORDER.indexOf(a);
    const ib = BOX_SECTION_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });

/** Najpierw Allegro (channel.platform_id === 1), potem najstarsze (prepared_at rosnąco), na końcu id. */

const sortFastShipmentRowsAllegroFirstThenOldest = (rows) =>
  [...rows].sort((a, b) => {
    const allegroRank = (row) => (row.channel?.platform_id === 1 ? 0 : 1);
    const ra = allegroRank(a);
    const rb = allegroRank(b);
    if (ra !== rb) return ra - rb;
    const ta = a.prepared_at ? new Date(a.prepared_at).getTime() : Infinity;
    const tb = b.prepared_at ? new Date(b.prepared_at).getTime() : Infinity;
    if (ta !== tb) return ta - tb;
    return (a.id ?? 0) - (b.id ?? 0);
  });

const FastShipmentGdns = () => {
  const [grouped, setGrouped] = useState({});
  const [active_packs, setActivePacks] = useState([]);
  const [is_loading, setIsLoading] = useState(true);
  const [modal_config, setModalConfig] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await client_v2.get("/gdns/fast_shipments");
      const payload = res.data || {};
      setActivePacks(
        Array.isArray(payload.active_packs) ? payload.active_packs : [],
      );
      const sectionKeys = Object.keys(payload).filter(
        (k) => k !== "active_packs",
      );
      const next = {};
      sortSectionKeys(sectionKeys).forEach((key) => {
        const raw = Array.isArray(payload[key]) ? payload[key] : [];
        next[key] = sortFastShipmentRowsAllegroFirstThenOldest(raw);
      });
      setGrouped(next);
    } catch (error) {
      toastr.error("Błąd", getApiErrorMessage(error));
      setGrouped({});
      setActivePacks([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (is_loading) {
    return (
      <Main
        page={{
          name: "Szybka wysyłka",
          icon: <GdnIcon />,
          breadcrumbs: [
            {
              name: "Wydania",
              path: "/gdns",
              icon: "export",
            },
            { name: "Szybka wysyłka" },
          ],
        }}
      >
        <PanelLoader />
      </Main>
    );
  }

  const orderedKeys = sortSectionKeys(Object.keys(grouped));

  return (
    <Main
      page={{
        name: "Szybka wysyłka",
        icon: <GdnIcon />,
        breadcrumbs: [
          {
            name: "Wydania",
            path: "/gdns",
            icon: "export",
          },
          { name: "Szybka wysyłka" },
        ],
      }}
    >
      <ActiveFastShipmentPacks rows={active_packs} />
      {orderedKeys.map((box_key) => (
        <GdnCartBox
          key={box_key}
          box_key={box_key}
          rows={grouped[box_key] || []}
          setModalConfig={setModalConfig}
        />
      ))}
      {modal_config && (
        <CreateFastShipmentModal
          handleClose={() => setModalConfig(null)}
          cart_box_size={modal_config.cart_box_size}
          dispatch_due_today={modal_config.dispatch_due_today}
          gdn_id={modal_config.gdn_id}
          expano_oms_id={modal_config.expano_oms_id}
          onCreated={loadData}
        />
      )}
    </Main>
  );
};

export default FastShipmentGdns;
